import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Container, Row, Col, Card, CardBody, Button, Badge } from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { vendorsData } from "../../../common/data/vendors"
import { useTranslation } from "react-i18next"

export const VendorDetails = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  // Find vendor by ID
  const vendor = vendorsData.find(v => v.id === id)
  
  const [copied, setCopied] = useState(false)

  const handleCopyId = () => {
    if (vendor) {
      navigator.clipboard.writeText(vendor.id)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleEdit = () => {
    console.log("Edit vendor:", vendor?.id)
    // TODO: Open edit modal or navigate to edit page
  }

  if (!vendor) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h4>Vendor not found</h4>
            <Button color="primary" onClick={() => navigate("/vendors")}>
              Back to Vendors
            </Button>
          </div>
        </Container>
      </div>
    )
  }

  const getVerificationBadgeColor = (verification: string) => {
    switch (verification) {
      case "Verified":
        return "success"
      case "Pending":
        return "warning"
      case "Not Verified":
        return "danger"
      default:
        return "secondary"
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs 
          title={t("vendors_vendors")} 
          breadcrumbItem={t("vendors_vendorDetails")} 
        />

        {/* Page Header */}
        <Row className="mb-4">
          <Col lg="12">
            <Card className="shadow-md rounded-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="avatar-lg rounded-circle me-3">
                      <img 
                        src={vendor.avatar} 
                        alt={vendor.name}
                        className="rounded-circle"
                        style={{ width: "64px", height: "64px", objectFit: "cover" }}
                      />
                    </div>
                    <div>
                      <h4 className="mb-1 fw-semibold">{vendor.name}</h4>
                      <div className="d-flex align-items-center">
                        <span className="text-muted me-2">{vendor.id}</span>
                        <Button 
                          color="link" 
                          size="sm" 
                          className="p-0"
                          onClick={handleCopyId}
                        >
                          <i className={`bx ${copied ? 'bx-check' : 'bx-copy'} font-size-16`}></i>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge 
                      color={vendor.status === "Active" ? "success" : "danger"}
                      className="badge-soft-lg font-size-14 px-3 py-2"
                    >
                      {vendor.status}
                    </Badge>
                    <Button 
                      className="bg-gradiant-blue border-0"
                      onClick={handleEdit}
                    >
                      <i className="bx bx-edit me-1"></i>
                      Edit Vendor
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Left Column */}
          <Col lg="8">
            {/* Basic Information */}
            <Card className="shadow-md rounded-3 mb-4">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Basic Information</h5>
                <Row>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Vendor Name</p>
                    <p className="mb-0 fw-medium">{vendor.name}</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Company Name</p>
                    <p className="mb-0 fw-medium">{vendor.companyName}</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Location</p>
                    <p className="mb-0 fw-medium">
                      <i className="bx bx-map me-1"></i>
                      {vendor.location}
                    </p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Zone</p>
                    <p className="mb-0 fw-medium">{vendor.zone || "N/A"}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Contact Information */}
            <Card className="shadow-md rounded-3 mb-4">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Contact Information</h5>
                <Row>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Contact Person</p>
                    <p className="mb-0 fw-medium">{vendor.contactPerson || "N/A"}</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Email</p>
                    <p className="mb-0 fw-medium">
                      <a href={`mailto:${vendor.email}`} className="text-primary">
                        <i className="bx bx-envelope me-1"></i>
                        {vendor.email}
                      </a>
                    </p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Phone Number</p>
                    <p className="mb-0 fw-medium">
                      <a href={`tel:${vendor.phone}`} className="text-primary">
                        <i className="bx bx-phone me-1"></i>
                        {vendor.phone}
                      </a>
                    </p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Verification Documents */}
            <Card className="shadow-md rounded-3 mb-4">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Verification Documents</h5>
                <Row>
                  <Col md="6" className="mb-3">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm rounded bg-light me-3 d-flex align-items-center justify-content-center">
                          <i className="bx bx-file font-size-20 text-primary"></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium">Commercial Registration</p>
                          <p className="text-muted mb-0 font-size-12">PDF • 2.4 MB</p>
                        </div>
                        <Button color="link" size="sm" className="p-0">
                          <i className="bx bx-download font-size-18"></i>
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-3">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm rounded bg-light me-3 d-flex align-items-center justify-content-center">
                          <i className="bx bx-file font-size-20 text-primary"></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium">Commercial Permit</p>
                          <p className="text-muted mb-0 font-size-12">PDF • 1.8 MB</p>
                        </div>
                        <Button color="link" size="sm" className="p-0">
                          <i className="bx bx-download font-size-18"></i>
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-3">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm rounded bg-light me-3 d-flex align-items-center justify-content-center">
                          <i className="bx bx-file font-size-20 text-primary"></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium">Tax Registration</p>
                          <p className="text-muted mb-0 font-size-12">PDF • 1.2 MB</p>
                        </div>
                        <Button color="link" size="sm" className="p-0">
                          <i className="bx bx-download font-size-18"></i>
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-3">
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm rounded bg-light me-3 d-flex align-items-center justify-content-center">
                          <i className="bx bx-file font-size-20 text-primary"></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-0 fw-medium">Computer Card</p>
                          <p className="text-muted mb-0 font-size-12">PDF • 0.9 MB</p>
                        </div>
                        <Button color="link" size="sm" className="p-0">
                          <i className="bx bx-download font-size-18"></i>
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Payment Information */}
            <Card className="shadow-md rounded-3">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Payment Information</h5>
                <Row>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Payment Method</p>
                    <p className="mb-0 fw-medium">Bank Transfer</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Account Number</p>
                    <p className="mb-0 fw-medium">XXXX-XXXX-1234</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Commission Rate (Bundle)</p>
                    <p className="mb-0 fw-medium">{vendor.revenueShare}</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Commission Rate (One-Time)</p>
                    <p className="mb-0 fw-medium">15%</p>
                  </Col>
                  <Col md="6" className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Payout Cycle</p>
                    <p className="mb-0 fw-medium">Monthly</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Right Column */}
          <Col lg="4">
            {/* Statistics */}
            <Card className="shadow-md rounded-3 mb-4">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Statistics</h5>
                <div className="mb-4 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                  <p className="text-muted mb-1 font-size-12">Total Orders</p>
                  <h4 className="mb-0 fw-semibold">{vendor.orders}</h4>
                </div>
                <div className="mb-4 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                  <p className="text-muted mb-1 font-size-12">Rating</p>
                  <div className="d-flex align-items-center">
                    <h4 className="mb-0 fw-semibold me-2">{vendor.rating}</h4>
                    <div className="text-warning">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i}
                          className={`bx ${i < Math.floor(vendor.rating) ? 'bxs-star' : 'bx-star'}`}
                          style={{ fontSize: "16px" }}
                        ></i>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mb-4 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                  <p className="text-muted mb-1 font-size-12">Revenue Share</p>
                  <h4 className="mb-0 fw-semibold">{vendor.revenueShare}</h4>
                </div>
                <div>
                  <p className="text-muted mb-1 font-size-12">Verification Status</p>
                  <Badge 
                    color={getVerificationBadgeColor(vendor.verification)}
                    className="badge-soft"
                  >
                    <i className="bx bx-check-circle me-1"></i>
                    {vendor.verification}
                  </Badge>
                </div>
              </CardBody>
            </Card>

            {/* Visibility Settings */}
            <Card className="shadow-md rounded-3">
              <CardBody>
                <h5 className="mb-4 fw-semibold">Visibility & Features</h5>
                <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fw-medium">Vendor Visibility</p>
                      <p className="text-muted mb-0 font-size-11">Visible to customers</p>
                    </div>
                    <Badge color="success" className="badge-soft">
                      <i className="bx bx-check"></i>
                    </Badge>
                  </div>
                </div>
                <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fw-medium">Verified Vendor</p>
                      <p className="text-muted mb-0 font-size-11">Verification status</p>
                    </div>
                    <Badge 
                      color={vendor.verification === "Verified" ? "success" : "warning"}
                      className="badge-soft"
                    >
                      <i className="bx bx-check"></i>
                    </Badge>
                  </div>
                </div>
                <div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fw-medium">Auto-Approval</p>
                      <p className="text-muted mb-0 font-size-11">Product auto-approval</p>
                    </div>
                    <Badge color="danger" className="badge-soft">
                      <i className="bx bx-x"></i>
                    </Badge>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
